import type { Envelope, EnvelopeScope } from '@/domain'

import type { Database } from './database.types'
import type { CentimeClient } from './supabase'

type EnvelopeRow = Database['public']['Tables']['envelopes']['Row']
type EnvelopeInsert = Database['public']['Tables']['envelopes']['Insert']

/** `null` en base signifie « toutes les enveloppes ». */
function scopeFromRow(value: string[] | null): EnvelopeScope {
  return value ?? 'all'
}

function scopeToRow(scope: EnvelopeScope): string[] | null {
  return scope === 'all' ? null : [...scope]
}

export function envelopeFromRow(row: EnvelopeRow): Envelope {
  return {
    id: row.id,
    name: row.name,
    icon: row.icon,
    kind: row.kind,
    isProtected: row.is_protected,
    monthlyTarget: row.monthly_target,
    rolloverPolicy: row.rollover_policy,
    transferRules: {
      allowOutgoing: row.allow_outgoing,
      allowIncoming: row.allow_incoming,
      destinations: scopeFromRow(row.transfer_destinations),
      sources: scopeFromRow(row.transfer_sources),
      requireVerification: row.require_verification,
    },
    allowOverdraft: row.allow_overdraft,
    sortOrder: row.sort_order,
    archivedAt: row.archived_at,
  }
}

export function envelopeToRow(envelope: Envelope): EnvelopeInsert {
  return {
    id: envelope.id,
    name: envelope.name.trim(),
    icon: envelope.icon,
    kind: envelope.kind,
    is_protected: envelope.isProtected,
    monthly_target: envelope.monthlyTarget,
    rollover_policy: envelope.rolloverPolicy,
    allow_outgoing: envelope.transferRules.allowOutgoing,
    allow_incoming: envelope.transferRules.allowIncoming,
    transfer_destinations: scopeToRow(envelope.transferRules.destinations),
    transfer_sources: scopeToRow(envelope.transferRules.sources),
    require_verification: envelope.transferRules.requireVerification,
    allow_overdraft: envelope.allowOverdraft,
    sort_order: envelope.sortOrder,
    archived_at: envelope.archivedAt,
  }
}

export async function listEnvelopes(client: CentimeClient): Promise<Envelope[]> {
  const { data, error } = await client.from('envelopes').select('*').order('sort_order')
  if (error) throw error
  return data.map(envelopeFromRow)
}

/** Crée ou met à jour une enveloppe (l'identifiant est généré côté client). */
export async function saveEnvelope(client: CentimeClient, envelope: Envelope): Promise<Envelope> {
  const { data, error } = await client
    .from('envelopes')
    .upsert(envelopeToRow(envelope))
    .select('*')
    .single()
  if (error) throw error
  return envelopeFromRow(data)
}
